import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Form,
    InputGroup
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";

export default function SocieteInfos() {
    const navigate = useNavigate();
    const [societe, setSociete] = useState({
        nom: '',
        adresse: '',
        telephone: '',
        email: '',
        site_web: '',
        rc: '',
        nif: '',
        patente: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetchSociete();
    }, []);

    const fetchSociete = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setSociete(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSociete({
            ...societe,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        setSaving(true);
        setError(null);
        setSuccess(false);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'update_societe',
                        ...societe
                    })
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
            } else {
                setError("Erreur lors de la sauvegarde");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des informations...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-building-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Informations société</h4>
                                    <p className="text-muted small mb-0">Gérez les informations de votre entreprise</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Alertes modernes */}
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            Informations mises à jour avec succès
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}

                    {/* Formulaire */}
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                            <h5 className="mb-0 text-white">
                                <i className="ri-building-line me-2"></i>
                                Coordonnées de l'entreprise
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-4">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Nom de la société</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="nom"
                                                value={societe.nom}
                                                onChange={handleChange}
                                                placeholder="Ma Société"
                                                required
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Site web</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">
                                                    <i className="ri-global-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="site_web"
                                                    value={societe.site_web}
                                                    onChange={handleChange}
                                                    placeholder="www.masociete.com"
                                                    className="border-2"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="g-4 mt-2">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Adresse</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">
                                                    <i className="ri-map-pin-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    name="adresse"
                                                    value={societe.adresse}
                                                    onChange={handleChange}
                                                    placeholder="Adresse complète"
                                                    className="border-2"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="g-4 mt-2">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Téléphone</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">
                                                    <i className="ri-phone-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="telephone"
                                                    value={societe.telephone}
                                                    onChange={handleChange}
                                                    placeholder="+241 XX XX XX XX"
                                                    className="border-2"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Email</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">
                                                    <i className="ri-mail-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={societe.email}
                                                    onChange={handleChange}
                                                    placeholder="contact@masociete.com"
                                                    className="border-2"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-4" style={{ borderColor: '#e2e8f0' }} />

                                <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                                    <i className="ri-file-copy-line me-2"></i>
                                    Identifiants juridiques
                                </h6>

                                <Row className="g-4">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">RC (Registre de Commerce)</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">
                                                    <i className="ri-barcode-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="rc"
                                                    value={societe.rc}
                                                    onChange={handleChange}
                                                    placeholder="RC123456"
                                                    className="border-2"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">NIF (Identification Fiscale)</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">
                                                    <i className="ri-file-text-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="nif"
                                                    value={societe.nif}
                                                    onChange={handleChange}
                                                    placeholder="NIF12345678"
                                                    className="border-2"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Patente</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">
                                                    <i className="ri-passport-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="patente"
                                                    value={societe.patente}
                                                    onChange={handleChange}
                                                    placeholder="PAT123456"
                                                    className="border-2"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                                    <Button 
                                        type="submit" 
                                        variant="primary" 
                                        className="rounded-pill px-5 py-2"
                                        disabled={saving}
                                        style={{ background: '#21ACCD', border: 'none' }}
                                    >
                                        {saving ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Sauvegarde...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-line me-2"></i>
                                                Enregistrer
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
            `}</style>
        </>
    );
}